import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/audit";
import { claimPendingPrediction, readPendingPrediction } from "@/lib/predictions/guestPrediction";
import { recordEngagement } from "@/lib/push/pushPermission";
import { getDeviceHash } from "@/lib/antifraud/deviceFingerprint";
import {
  recordDeviceSignal,
  claimGuestPredictionXp,
} from "@/lib/antifraud/antifraud.functions";

export const Route = createFileRoute("/auth/callback")({
  head: () => ({
    meta: [{ title: "Verificando · VerseSport" }],
  }),
  component: AuthCallback,
});

function AuthCallback() {
  const navigate = useNavigate();
  const [msg, setMsg] = useState("Verificando tu sesión…");

  useEffect(() => {
    let cancelled = false;
    const NAV_TIMEOUT_MS = 2500;
    let navigated = false;
    const goNationWars = () => {
      if (navigated || cancelled) return;
      navigated = true;
      navigate({ to: "/nation-wars" });
    };
    const run = async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      if (data.session) {
        let expected: string | undefined;
        try { expected = window.localStorage.getItem("vs:pending_signup_email")?.trim().toLowerCase() ?? undefined; } catch { /* noop */ }
        const actual = data.session.user?.email?.trim().toLowerCase();
        if (expected && actual && actual !== expected) {
          void trackEvent("signup_identity_mismatch", { expected, actual, surface: "auth_callback" });
          try { await supabase.auth.signOut(); } catch { /* noop */ }
          setMsg("La sesión activa pertenece a otra cuenta. Inicia sesión con tu email recién registrado.");
          setTimeout(() => navigate({ to: "/login", search: { from: undefined } }), 1500);
          return;
        }
        try { window.localStorage.removeItem("vs:pending_signup_email"); } catch { /* noop */ }
        // PR-34B — record signup_completed signal (privacy-safe device hash).
        try {
          const deviceHash = await getDeviceHash();
          if (deviceHash) {
            await recordDeviceSignal({
              data: { deviceHash, kind: "signup_completed" },
            });
          }
        } catch { /* anti-fraud signals are best-effort */ }
        // PR-32 — One Tap Claim Engine. If the user came from the guest
        // micro-prediction, finish the claim automatically without forcing
        // them through the email form. Safe when no pending exists.
        // PR-38B-A — the full claim pipeline is awaited (or bounded by
        // NAV_TIMEOUT_MS) so the welcome toast cannot race the navigation.
        const claimFlow = (async () => {
          const pending = readPendingPrediction();
          if (pending) {
            let variant: "A" | "B" | null = null;
            let oauthVariant: "A" | "B" | null = null;
            try {
              const v = window.localStorage.getItem("vs_claim_xp_variant");
              if (v === "A" || v === "B") variant = v;
              const o = window.localStorage.getItem("vs_claim_xp_oauth_variant");
              if (o === "A" || o === "B") oauthVariant = o;
            } catch { /* noop */ }
            // PR-34B — server-side guest XP claim with device cooldown / daily cap.
            try {
              const deviceHash = await getDeviceHash();
              if (deviceHash) {
                const idempotencyKey = `guest_claim:${data.session.user.id}:${new Date().toISOString().slice(0, 10)}`;
                const res = await claimGuestPredictionXp({
                  data: { deviceHash, idempotencyKey },
                });
                await recordDeviceSignal({
                  data: {
                    deviceHash,
                    kind: "xp_claim",
                    metadata: { reason: "pending_prediction_claim", ok: res.ok, why: res.reason ?? null },
                  },
                });
              }
            } catch { /* best-effort */ }
            // Local-state mutations happen AFTER server confirmation so the
            // welcome toast renders against the final XP balance.
            try { claimPendingPrediction(); } catch { /* noop */ }
            try { recordEngagement("prediction_submit"); } catch { /* noop */ }
            void trackEvent("oauth_claim_completed", {
              variant,
              oauth_variant: oauthVariant,
              choice: pending.choice,
              suggested_nation: pending.suggested_nation,
            });
            void trackEvent("claim_xp_signup_completed", {
              variant,
              choice: pending.choice,
              suggested_nation: pending.suggested_nation,
              via: "oauth",
            });
          }
        })().catch((e) => {
          console.warn("[auth.callback] claim_pending_failed", e);
        });
        setMsg("¡Bienvenido!");
        // P0.1 — push the verified user straight into the activation
        // funnel (NationGate fires here if selection_team is NULL).
        // PR-38B-A — wait for the claim to settle, capped at NAV_TIMEOUT_MS
        // so degraded backends never strand the user on this screen.
        const timeout = new Promise<void>((resolve) => setTimeout(resolve, NAV_TIMEOUT_MS));
        await Promise.race([claimFlow, timeout]);
        goNationWars();
      } else {
        setMsg("No pudimos iniciar tu sesión. Intenta de nuevo.");
        setTimeout(() => navigate({ to: "/login", search: { from: undefined } }), 1500);
      }
    };
    void run();
    return () => { cancelled = true; };
  }, [navigate]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4">
      <p className="text-sm text-muted-foreground">{msg}</p>
    </main>
  );
}